"use client";

import Image from "next/image";
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import type { Lang } from "@/lib/lang";
import { COPY } from "./copy";
import { Overline, usePrefersReducedMotion } from "./ui";

/*
 * The product's real trick, shown rather than told: the camera keeps the whole
 * pitch in one 7168×2160 panorama, and its AI director moves a 16:9 window
 * across it to cut a followed video. Scrolling drives the window; the inset is
 * literally that window, magnified — the "broadcast".
 */

const PANO_W = 4200;
const PANO_H = 1782;
const ASPECT = PANO_W / PANO_H;
const CW = 0.3; // window width, as a fraction of the panorama
const CH = CW * (9 / 16) * ASPECT; // window height that keeps the cut 16:9

// The play, in panorama coordinates: build-up in the left half, a switch
// through midfield, the attack down the right.
const KEYS = [0, 0.16, 0.38, 0.6, 0.82, 1];
const PATH_X = [0.25, 0.25, 0.4, 0.55, 0.71, 0.75];
const PATH_Y = [0.63, 0.63, 0.64, 0.61, 0.6, 0.6];

const clampX = (v: number) => Math.min(1 - CW / 2, Math.max(CW / 2, v));
const clampY = (v: number) => Math.min(1 - CH / 2, Math.max(CH / 2, v));

function timecode(p: number) {
  const s = Math.round(61 * 60 + 40 + p * 196);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function Director({ lang }: { lang: Lang }) {
  const d = COPY.director;
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 32, mass: 0.35 });
  const p = reduced ? scrollYProgress : smooth;

  const cx = useTransform(p, KEYS, PATH_X);
  const cy = useTransform(p, KEYS, PATH_Y);
  const left = useTransform(cx, (v) => `${(clampX(v) - CW / 2) * 100}%`);
  const top = useTransform(cy, (v) => `${(clampY(v) - CH / 2) * 100}%`);
  const insetX = useTransform(cx, (v) => `${-clampX(v) * 100}%`);
  const insetY = useTransform(cy, (v) => `${-clampY(v) * 100}%`);
  const clock = useTransform(p, timecode);
  const bar = useTransform(p, [0, 1], ["0%", "100%"]);

  const [step, setStep] = useState(0);
  // Captions follow the scroll position itself, not the smoothed frame.
  useMotionValueEvent(scrollYProgress, "change", (v) => setStep(v < 0.3 ? 0 : v < 0.62 ? 1 : 2));

  return (
    <section id="director" ref={ref} className="relative h-[290vh] lg:h-[340vh]" aria-label={d.title[lang]}>
      <div className="sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden pt-16 sm:pt-[72px]">
        <div className="mx-auto w-full max-w-[1360px] px-5 sm:px-8">
          <Overline gold>{d.over[lang]}</Overline>
          <h2 className="sn-wide mt-3 max-w-[24ch] text-[clamp(26px,3.6vw,54px)]">{d.title[lang]}</h2>
        </div>

        <div className="relative mx-auto mt-5 w-full lg:mt-6" style={{ maxWidth: "min(100%, calc((100svh - 410px) * 2.357))" }}>
          <div className="relative w-full overflow-hidden lg:rounded-[28px]" style={{ aspectRatio: `${PANO_W} / ${PANO_H}` }}>
            <Image src="/landing/panorama.webp" alt={d.alt[lang]} fill sizes="100vw" className="object-cover" />
            {/* HUD: what the camera itself is doing. */}
            <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full px-3 py-1.5 sm:left-5 sm:top-5" style={{ background: "rgba(5,7,13,.6)", backdropFilter: "blur(10px)" }}>
              <span className="sn-rec h-2 w-2 rounded-full" style={{ background: "var(--sn-bad)" }} />
              <span className="sn-num text-[11px] sm:text-[12px]">REC</span>
              <motion.span className="sn-num text-[11px] sm:text-[12px]" style={{ color: "var(--sn-fg2)" }}>{clock}</motion.span>
            </div>
            <div className="absolute right-3 top-3 hidden rounded-full px-3 py-1.5 sm:right-5 sm:top-5 sm:block" style={{ background: "rgba(5,7,13,.6)", backdropFilter: "blur(10px)" }}>
              <span className="sn-num text-[12px]" style={{ color: "var(--sn-fg2)" }}>{d.panorama[lang]}</span>
            </div>

            {/* The AI director's window. The huge shadow dims everything it is not filming. */}
            <motion.div
              className="absolute rounded-[6px] sm:rounded-[10px]"
              style={{
                left, top, width: `${CW * 100}%`, height: `${CH * 100}%`,
                boxShadow: "0 0 0 1.5px var(--sn-gold), 0 0 0 200vmax rgba(5,7,13,.5), 0 0 40px rgba(226,189,114,.35)",
              }}
            >
              {(["left-0 top-0 border-l-2 border-t-2", "right-0 top-0 border-r-2 border-t-2", "left-0 bottom-0 border-l-2 border-b-2", "right-0 bottom-0 border-r-2 border-b-2"] as const).map((pos) => (
                <span key={pos} className={`absolute h-3 w-3 sm:h-5 sm:w-5 ${pos}`} style={{ borderColor: "#fff", margin: -4 }} />
              ))}
              <span className="sn-num absolute -top-6 left-0 whitespace-nowrap text-[10px] tracking-[0.14em] sm:-top-7 sm:text-[11px]" style={{ color: "var(--sn-gold)" }}>
                {d.tracking[lang].toUpperCase()}
              </span>
            </motion.div>
          </div>

          {/* The broadcast: that same window, magnified. */}
          <div className="relative z-10 mx-5 mt-3 sm:mx-8 lg:absolute lg:-bottom-[14%] lg:right-[3%] lg:m-0 lg:w-[34%]">
            <div className="relative aspect-video overflow-hidden rounded-[16px] sm:rounded-[20px]" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,.85), 0 0 0 1px rgba(255,255,255,.14)" }}>
              <motion.img
                src="/landing/panorama.webp"
                alt=""
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 max-w-none"
                style={{ width: `${100 / CW}%`, x: insetX, y: insetY }}
                draggable={false}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,13,.35) 0%, rgba(5,7,13,0) 30%, rgba(5,7,13,0) 70%, rgba(5,7,13,.5) 100%)" }} />
              <div className="absolute left-3 top-3 flex items-center gap-2">
                <span className="sn-num rounded-[6px] px-2 py-0.5 text-[10.5px] tracking-[0.12em]" style={{ background: "var(--sn-bad)", color: "#fff" }}>LIVE</span>
                <span className="sn-over !text-[10px] !text-white">{d.broadcast[lang]}</span>
              </div>
              <div className="absolute inset-x-3 bottom-3 flex items-center gap-3">
                <span className="sn-num text-[11px]">Academia Norte 2–1 Atlético Sur</span>
                <span className="h-[3px] flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,.2)" }}>
                  <motion.span className="block h-full rounded-full" style={{ width: bar, background: "#fff" }} />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-6 w-full max-w-[1360px] px-5 sm:px-8 lg:mt-8">
          <div className="lg:max-w-[52%]">
            <div className="flex gap-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-[3px] w-10 rounded-full transition-colors duration-500" style={{ background: i <= step ? "var(--sn-gold)" : "rgba(255,255,255,.18)" }} />
              ))}
            </div>
            {/* Three stacked captions that crossfade — robust to fast scrolling. */}
            <div className="relative mt-4 min-h-[112px] sm:min-h-[96px]">
              {d.steps.map((st, i) => (
                <div
                  key={st.title.en}
                  aria-hidden={i !== step}
                  className="absolute inset-x-0 top-0"
                  style={{
                    opacity: i === step ? 1 : 0,
                    transform: i === step ? "none" : `translateY(${i < step ? -10 : 12}px)`,
                    // The outgoing caption clears before the next one arrives.
                    transition: i === step ? "opacity .45s ease-out .18s, transform .45s ease-out .18s" : "opacity .18s ease-in, transform .18s ease-in",
                  }}
                >
                  <p className="text-[19px] font-semibold sm:text-[21px]">{st.title[lang]}</p>
                  <p className="mt-1.5 max-w-[34rem] text-[15px] leading-relaxed sm:text-[16px]" style={{ color: "var(--sn-fg2)" }}>{st.body[lang]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
